import { SlicePipe } from "@angular/common";
import { Component, computed, effect, inject, Injector, input } from "@angular/core";
import { S25ItemComponent } from "../../../modules/s25-item/s25.item.component";
import { S25IconComponent } from "../../../modules/s25-icon/s25.icon.component";
import { S25ObjectSearchModule } from "../../../modules/s25-object-search/s25.object.search.module";
import { ModalService } from "../../../modules/modal/modal.service";
import { EventFormObjectOccurrencesTableModal } from "../../../modules/modal/modals/event.form.object.occurrences.table.modal.component";
import { EventFormStore } from "../event.form.store";
import { EventFormOccurrenceUtil } from "../../../modules/s25-event-creation-form/occurrences/event.form.occurrence.util";
import { S25Datefilter } from "../../../modules/s25-dateformat/s25.datefilter.service";
import { Item } from "../../../pojo/Item";
import { UserStore } from "../../../stores/user.store";
import { S25Const } from "../../../util/s25-const";
import { isDefined } from "../../../util/core";
import { S25Bool } from "../../../util/core/s25.bool";
import { S25Date } from "../../../util/core/s25.date";
import { S25Number } from "../../../util/core/s25.number";
import { EventFormObjectOccurrencesTableComponent } from "../object-occurrences/event.form.object.occurrences.table.component";
import { EventForm } from "..";
import { S25FavoriteButtonComponent } from "../../s25-favorite-button/s25.favorite.button.component";

@Component({
    selector: "s25-event-form-object-details",
    standalone: true,
    imports: [
        SlicePipe,
        S25ItemComponent,
        S25FavoriteButtonComponent,
        S25IconComponent,
        S25ObjectSearchModule,
        EventFormObjectOccurrencesTableComponent,
    ],
    template: `
        <s25-item
            class="c-displayInlineBlock"
            [itemId]="item().itemId"
            [itemTypeId]="item().itemTypeId"
            [itemName]="item().itemName"
            [includeTypeIcon]="true"
        />

        <s25-ng-favorite-button
            class="rose-added-object--star"
            [itemId]="item().itemId"
            [itemTypeId]="item().itemTypeId"
        />

        @if (isLocation()) {
            <div class="rose-added-object--details-wrapper">
                @if (item().formal_name) {
                    <div class="c-displayInlineBlock c-margin-left--half">
                        <span class="rose-added-object--small">{{ item().formal_name }}</span>
                    </div>
                }
                @if (minLayoutCap()) {
                    <div class="c-displayInlineBlock c-margin-left--half">
                        <span class="ngBold">Capacity: </span>
                        <span class="rose-added-object--small">{{ minLayoutCap() }}</span>
                    </div>
                }
                @if (alwaysShared()) {
                    <div class="c-displayInlineBlock c-margin-left--half">
                        <span class="rose-added-object--small ngBold">Always Shared</span>
                    </div>
                }
                @if (headCount() > minLayoutCap()) {
                    <div class="ngBold ngRed">
                        The capacity of this room/layout ({{ minLayoutCap() }}) is smaller than your head count ({{
                            headCount()
                        }})!
                    </div>
                }
            </div>
        }

        @if (includedDates().length !== occurrenceCount()) {
            <div>
                <div>Included dates:</div>
                @if (includedDates().length) {
                    <div>
                        @for (evStartDt of includedDates(); track $index) {
                            <span
                                >{{ formatOccDate(evStartDt)
                                }}{{ $index === includedDates().length - 1 ? "" : ", " }}</span
                            >
                        }
                    </div>
                } @else {
                    <div>None</div>
                }
            </div>
        }

        @if (bufferWarningDates().length) {
            <div class="ngRed ngBold">
                This location requires an advanced notice of: {{ item().assign_buffer_string }}. In order to save,
                please adjust or exclude these occurrence date(s):
                <ul>
                    @for (warningDate of bufferWarningDates(); track $index) {
                        <li>{{ formatOccDate(warningDate) }}</li>
                    }
                </ul>
            </div>
        }

        @if (apWarningDates().length) {
            <div class="ngRed ngBold">
                You cannot request or reserve this {{ objectNoun() }} on these selected dates:
                <ul>
                    @for (warningDate of apWarningDates(); track $index) {
                        <li>{{ formatOccDate(warningDate) }}</li>
                    }
                </ul>
                Please adjust or exclude the occurrence date(s).
            </div>
        }

        @if (isSingleOccurrence()) {
            <s25-ng-event-form-object-occurrences-table
                [objOccurrences]="objOccurrences()"
                [inline]="true"
                [layouts]="item().layouts || []"
                [alwaysShared]="alwaysShared()"
            />
        }

        <div class="rose-added-object--action-buttons c-margin-top--half">
            <button
                class="aw-button aw-button--danger aw-button--flat"
                [attr.aria-label]="'Remove ' + objectNoun() + ' ' + item().itemName"
                (click)="removeObject()"
            >
                Remove
            </button>
            <button class="aw-button aw-button--outline ngObjOccViewOcc" (click)="viewOccurrences()">
                View Occurrences
            </button>
        </div>

        @if (isMultiOccurrence()) {
            <div class="c-displayInlineBlock rose--added-object__conflict--wrapper c-margin-top--half">
                @if (exceedingQuantity().length) {
                    <div class="cn-alert">
                        <div class="cn-alert__icon cn-icon cn-alert--warning" name="alert--info">
                            <s25-ng-icon [type]="'warningYield'" [ariaLabel]="'Excess Resource Reservation Warning'" />
                        </div>
                        <div class="cn-alert__label">
                            <div class="cn-alert--warning ngBold">
                                Exceeding available resources in timeslot{{
                                    exceedingQuantity().length > 1 ? "s" : ""
                                }}:
                            </div>
                            @for (occ of exceedingQuantity() | slice: 0 : 5; track $index) {
                                <p class="ngObjOccConflict">{{ occ.start }} - {{ occ.end }}</p>
                            }
                        </div>
                    </div>
                }

                @if (conflicts().length) {
                    <div class="cn-alert">
                        <div class="cn-alert__icon cn-icon c-red" name="alert--info">
                            <s25-ng-icon [type]="'warningYield'" [ariaLabel]="'Conflicts Warning'" />
                        </div>
                        <div class="cn-alert__label">
                            <div class="c-red ngBold">Conflicts on:</div>
                            @for (conflict of conflicts() | slice: 0 : 5; track $index; let last = $last) {
                                <p class="ngObjOccConflict">
                                    {{ formatOccDate(conflict.candidateDate) }}{{ last ? "" : ", " }}
                                </p>
                            }
                            <s25-ng-object-conflict
                                [hasConflict]="true"
                                [data]="item()"
                                [buttonText]="'View All Conflict Details'"
                                [buttonClass]="'c-margin-top--quarter aw-button aw-button--danger--outline'"
                                [modalSize]="'md'"
                            />
                        </div>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .rose--added-object__conflict--wrapper {
            width: 100%;
            padding-left: 1em;
            display: grid;
            gap: 0.5em;

            &:empty {
                display: none;
            }
        }
        .cn-alert {
            border: none;
            padding: 0.25em;
            margin: 0;
            flex-wrap: wrap;

            &::before {
                content: none;
            }
        }
        .cn-alert__label {
            flex: none;
        }
    `,
})
export class EventFormObjectDetails {
    /**
     * don't use this directly, because it can be mutated in place.
     * instead, use {@link item}
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    readonly itemInput = input.required<EventForm.ObjectItem>({ alias: "item" });

    private readonly user = inject(UserStore);
    private readonly eventForm = inject(EventFormStore);

    /** The item is mutated in place by the availability checks, so this re-reads on every revision. */
    readonly item = computed(
        (): EventForm.ObjectItem => {
            this.eventForm.revision();
            return this.itemInput();
        },
        { equal: () => false },
    );
    /** Handed to the occurrences modal so it resolves this form's store rather than the root one. */
    private readonly injector = inject(Injector);

    /** This item's row per occurrence, rebuilt every revision. */
    readonly objOccurrences = computed(() => {
        const item = this.item();
        const formModel = this.eventForm.model();
        return this.eventForm.sortedOccurrences().map((occ) => formModel.getObjOcc(item, occ, true));
    });

    readonly isLocation = computed(() => this.item()?.itemTypeId === Item.Ids.Location);
    readonly alwaysShared = computed(() => S25Bool.toBool(this.item()?.always_shared));
    readonly objectNoun = computed(() => (this.isLocation() ? "location" : "resource"));

    readonly occurrenceCount = computed(() => this.eventForm.model().occurrences?.length ?? 0);
    readonly isSingleOccurrence = computed(() => this.occurrenceCount() === 1);
    readonly isMultiOccurrence = computed(() => this.occurrenceCount() > 1);

    readonly headCount = computed(() => {
        const itemDict = this.eventForm.model().itemDict;
        return S25Number.parseInt(itemDict?.expected?.data) || S25Number.parseInt(itemDict?.registered?.data);
    });

    readonly includedOccurrences = computed(() => this.objOccurrences().filter((objOcc) => objOcc.isIncluded));
    readonly includedDates = computed(() => this.includedOccurrences().map((objOcc) => objOcc.occ.evStartDt));

    readonly minLayoutCap = computed(() => {
        const layoutCapacities = this.includedOccurrences()
            .map((objOcc) => (objOcc.layout ? S25Number.parseInt(objOcc.layout.layout_capacity) : undefined))
            .filter((capacity) => isDefined(capacity));
        return Math.min(S25Number.parseInt(this.item()?.max_capacity) || 0, ...layoutCapacities);
    });

    /** Assignment policy and advance notice are ignored while the event's overrides are on. */
    readonly assignmentPolicyApplies = computed(() => {
        const formModel = this.eventForm.model();
        return this.isLocation() ? !formModel.overrideRmAp : !formModel.overrideRsAp;
    });

    readonly bufferWarningOccurrences = computed(() => {
        if (!this.assignmentPolicyApplies()) return [];
        const currentDateTime = S25Date.currentDateTime();
        return this.includedOccurrences().filter((objOcc) =>
            EventForm.ObjectOccurrence.needsAdvanceNotice(objOcc, currentDateTime),
        );
    });
    readonly bufferWarningDates = computed(() => this.bufferWarningOccurrences().map((objOcc) => objOcc.occ.evStartDt));

    /** An occurrence already blocked by advance notice is not reported as a policy warning too. */
    readonly apWarningOccurrences = computed(() => {
        if (!this.assignmentPolicyApplies()) return [];
        const bufferWarnings = new Set(this.bufferWarningOccurrences());
        return this.includedOccurrences().filter(
            (objOcc) => !bufferWarnings.has(objOcc) && EventForm.ObjectOccurrence.blockedByAssignmentPolicy(objOcc),
        );
    });
    readonly apWarningDates = computed(() => this.apWarningOccurrences().map((objOcc) => objOcc.occ.evStartDt));

    readonly conflicts = computed(() => {
        const checkedConflicts = this.item()?.checkedObj?.conflicts ?? [];
        return this.includedOccurrences().flatMap((objOcc) =>
            checkedConflicts.filter((conflict) => conflict.isReal && conflict.occUUID === objOcc.occ.uuid),
        );
    });

    readonly exceedingResourceQuantity = computed(() =>
        EventFormOccurrenceUtil.getExceedingResourceQuantity(this.item(), this.objOccurrences()),
    );

    readonly exceedingQuantity = computed(() => {
        return this.exceedingResourceQuantity().map((quantity) => {
            const startDate = S25Datefilter.transform(quantity.start, this.user.dateFormat()!);
            const endDate = S25Datefilter.transform(quantity.end, this.user.dateFormat()!);
            const endTime = S25Datefilter.transform(quantity.end, this.user.timeFormat()!);
            return {
                start: S25Datefilter.transform(quantity.start, this.user.dateTimeFormat()!),
                end: startDate === endDate ? endTime : `${endDate} ${endTime}`,
            };
        });
    });

    constructor() {
        /**
         * Inputs arrive after construction, so this waits for them, then stops. Stamping
         * `schedulerLocation` is what tells the rest of the form that the event scheduler came from
         * this location and must not be overridden as more locations are added.
         */
        const schedulerLocationEffect = effect(() => {
            const item = this.item();
            const formModel = this.eventForm.model();
            if (!item) return;
            // This is a one-shot effect once we have an item
            schedulerLocationEffect.destroy();
            const scheduler = formModel.itemDict?.contacts?.data?.find(
                (role) => role.role_id === S25Const.schedulerRole.event,
            );
            if (scheduler?.data?.itemId === item.scheduler?.itemId) {
                formModel.setSchedulerFromLocation?.(item);
                this.eventForm.bump();
            }
        });

        effect(() => this.syncApWarningUUIDs());
        effect(() => this.syncExceedingQuantity());
    }

    formatOccDate(value: Date | string | undefined): string {
        return S25Datefilter.transform(value, this.user.dateFormat()!) ?? "";
    }

    /** `removeObject` may confirm first, and bumps the revision itself once the item is spliced. */
    removeObject(): void {
        const item = this.item();
        const formModel = this.eventForm.model();
        if (!item) return;
        const isLocation = this.isLocation();
        void Promise.resolve(formModel.removeObject(item)).then((removed) => {
            if (removed === false) return;
            const itemModel = isLocation ? formModel.itemDict?.space : formModel.itemDict?.resource;
            itemModel?.action?.();
        });
    }

    viewOccurrences(): void {
        const formModel = this.eventForm.model();
        const item = this.item();
        if (!item) return;
        formModel.modal = EventFormObjectOccurrencesTableModal;
        void ModalService.byClass(EventFormObjectOccurrencesTableModal, {
            item,
            objOccurrences: this.objOccurrences(),
            title: item.itemName,
            injector: this.injector,
            onClosed: () => {
                let refreshPromise: Promise<unknown> = Promise.resolve();
                if (formModel.needsDetailsListRecreate) {
                    formModel.needsDetailsListRecreate = false;
                    refreshPromise = Promise.resolve(formModel.recreateDetailsList());
                }
                return refreshPromise.finally(() => {
                    formModel.modal = null;
                    this.eventForm.bump();
                });
            },
        });
    }

    /** Mirrors the derived policy warnings onto the item, where the occurrence code reads them. */
    private syncApWarningUUIDs(): void {
        const item = this.item();
        if (!item) return;
        const uuids = (item.apWarningUUIDs ??= new Set());
        const warnedUUIDs = new Set(this.apWarningOccurrences().map((objOcc) => objOcc.occ?.uuid));
        for (const objOcc of this.includedOccurrences()) {
            const uuid = objOcc.occ?.uuid;
            if (warnedUUIDs.has(uuid)) uuids.add(uuid);
            else uuids.delete(uuid);
        }
    }

    /** Mirrors the derived over-reservation windows onto the item, where the occurrence rows read them. */
    private syncExceedingQuantity(): void {
        const item = this.item();
        if (!item) return;
        (item.checkedObj ??= {}).exceedingQuantity = this.exceedingResourceQuantity();
    }
}
